import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, ShieldCheck, Truck } from "lucide-react";

export default function HomePage() {
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const { data: featured = [] } = useQuery({ queryKey: ["featured"], queryFn: async () => (await api.get("/products", { params: { featured: true } })).data });
    const { data: categories = [] } = useQuery({ queryKey: ["cats"], queryFn: async () => (await api.get("/categories")).data });

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden bg-bone2">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center py-14 lg:py-24">
                    <div className="animate-fade-up">
                        <p className="text-eyebrow mb-4">🇺🇸 Importado desde USA</p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]">{settings?.hero_title || "Estilo americano, entregado en Colombia"}</h1>
                        <p className="mt-5 text-ink/70 max-w-lg text-base">{settings?.hero_subtitle}</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/tienda" data-testid="hero-cta-shop" className="btn-ink inline-flex items-center gap-2">{settings?.hero_cta_label || "Ver Catálogo"} <ArrowRight className="w-4 h-4" /></Link>
                            <Link to="/tienda?oferta=1" className="btn-outline">Ver Ofertas</Link>
                        </div>
                    </div>
                    <div className="relative aspect-[4/5] rounded-lg overflow-hidden shadow-xl">
                        <img src={imageUrl(settings?.hero_image) || "/products/hero-main.jpg"} alt="Hero" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent" />
                    </div>
                </div>
            </section>

            {/* TRUST */}
            <section className="border-y border-sandLight bg-bone">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8 grid grid-cols-2 gap-6 text-center">
                    {[{ i: <ShieldCheck className="w-6 h-6" />, t: "100% Originales", s: "Traídos desde USA" },
                    { i: <Truck className="w-6 h-6" />, t: "Envíos", s: "A toda Colombia" }].map((x, i) => (
                        <div key={i} className="flex flex-col items-center gap-2 text-ink">{x.i}<p className="text-sm font-medium">{x.t}</p><p className="text-xs text-ink/60">{x.s}</p></div>
                    ))}
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
                <div className="flex items-end justify-between mb-8">
                    <div><p className="text-eyebrow">Explora</p><h2 className="font-display text-3xl sm:text-4xl mt-2">Categorías</h2></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                    {categories.map((c) => (
                        <Link key={c.id} to={`/tienda?categoria=${encodeURIComponent(c.name)}`} className="group relative aspect-[4/5] rounded-md overflow-hidden bg-sandLight">
                            {c.image && <img src={imageUrl(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                            <div className="absolute inset-0 bg-ink/25 group-hover:bg-ink/10 transition-colors" />
                            <div className="absolute bottom-4 left-4"><p className="font-display text-xl text-bone">{c.name}</p></div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED */}
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-6">
                <div className="flex items-end justify-between mb-8">
                    <div><p className="text-eyebrow">Selección</p><h2 className="font-display text-3xl sm:text-4xl mt-2">Productos Destacados</h2></div>
                    <Link to="/tienda" className="text-eyebrow text-ink hover:text-terracotta hidden sm:block">Ver todo →</Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10">
                    {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
            </section>

            {/* ABOUT */}
            {settings?.about_text && (
                <section className="bg-ink text-bone py-20 mt-8">
                    <div className="mx-auto max-w-3xl px-4 sm:px-8 text-center">
                        <p className="text-eyebrow text-bone/60">Nuestra historia</p>
                        <h2 className="font-display text-3xl sm:text-4xl mt-3">{settings.about_title}</h2>
                        <p className="mt-6 text-bone/80 leading-relaxed">{settings.about_text}</p>
                    </div>
                </section>
            )}

            {/* FAQ */}
            {settings?.faqs?.length > 0 && (
                <section className="mx-auto max-w-3xl px-4 sm:px-8 py-20">
                    <p className="text-eyebrow text-center">Ayuda</p>
                    <h2 className="font-display text-3xl sm:text-4xl mt-2 text-center">Preguntas Frecuentes</h2>
                    <div className="mt-10 divide-y divide-sandLight border-y border-sandLight">
                        {settings.faqs.map((f, i) => (
                            <details key={i} className="py-5 group">
                                <summary className="flex justify-between items-center cursor-pointer text-ink font-medium">{f.q}<span className="text-terracotta text-xl group-open:rotate-45 transition-transform">+</span></summary>
                                <p className="mt-3 text-ink/70 text-sm">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
